import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, money } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Section, F, btn } from "@/components/CaseBits";
import { MultiPdfPicker, checkPdfs, uploadFiling } from "@/components/MultiPdfPicker";

const KINDS = {
  statement_of_claim: { side: "claimant", label: "Statement of Claim" },
  statement_of_defence: { side: "respondent", label: "Statement of Defence" },
  counter_claim: { side: "respondent", label: "Counter-claim" },
};
const blankParty = { name: "", address: "", email: "", phone: "", representative: "" };

const PartyFields = ({ title, p, onChange, prefix }) => {
  const set = (k, v) => onChange({ ...p, [k]: v });
  return (
    <fieldset className="border border-border p-4 space-y-3" data-testid={`${prefix}-fields`}>
      <legend className="label-caps px-1">{title}</legend>
      <F label="Name *" required value={p.name} onChange={(v) => set("name", v)} testId={`${prefix}-name`} />
      <div className="space-y-2"><Label className="label-caps">Address *</Label>
        <Textarea required value={p.address} onChange={(e) => set("address", e.target.value)} className="rounded-none min-h-[64px]" data-testid={`${prefix}-address`} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Email" type="email" value={p.email} onChange={(v) => set("email", v)} testId={`${prefix}-email`} />
        <F label="Phone" value={p.phone} onChange={(v) => set("phone", v)} testId={`${prefix}-phone`} />
      </div>
      <F label="Authorised representative / counsel" value={p.representative} onChange={(v) => set("representative", v)} testId={`${prefix}-representative`} />
    </fieldset>
  );
};

const PleadingForm = ({ caseId, kind, kase, existing, onDone }) => {
  const isDefence = kind === "statement_of_defence" || kind === "counter_claim";
  const init = existing || {
    claimant: { ...blankParty, name: kase.claimant }, respondent: { ...blankParty, name: kase.respondent },
    claim_amount: kase.claim_amount || "", relief_sought: "", brief_facts: "", counter_claim_amount: "", preliminary_objections: "",
  };
  const [f, setF] = useState(init);
  const [files, setFiles] = useState([]);
  const [merge, setMerge] = useState(false);
  const [progress, setProgress] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const bad = checkPdfs(files);
    if (bad) return toast.error(bad);
    const body = { ...f, claim_amount: Number(f.claim_amount || 0),
      counter_claim_amount: f.counter_claim_amount === "" || f.counter_claim_amount == null ? null : Number(f.counter_claim_amount) };
    try {
      await api.put(`/cases/${caseId}/pleadings/${kind}`, body);
      const rows = await uploadFiling(caseId, `${KINDS[kind].label} — annexures`, "pleading", files, { merge, onProgress: setProgress });
      toast.success(`${KINDS[kind].label} ${existing ? "amended" : "filed"}${rows.length ? ` with ${rows.length} PDF(s)` : ""} — parties notified`);
      setFiles([]); setProgress(null);
      onDone();
    } catch (err) { toast.error(errMsg(err)); setProgress(null); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`pleading-form-${kind}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <PartyFields title="Claimant details" p={f.claimant} onChange={(v) => set("claimant", v)} prefix={`${kind}-claimant`} />
        <PartyFields title="Respondent details" p={f.respondent} onChange={(v) => set("respondent", v)} prefix={`${kind}-respondent`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <F label={isDefence ? "Claim amount admitted / in dispute (INR) *" : "Claim amount (INR) *"} type="number" required value={f.claim_amount}
          onChange={(v) => set("claim_amount", v)} testId={`${kind}-claim-amount`} />
        {isDefence && <F label="Counter-claim amount (INR)" type="number" value={f.counter_claim_amount ?? ""} onChange={(v) => set("counter_claim_amount", v)} testId={`${kind}-counter-claim`} />}
      </div>
      <div className="space-y-2"><Label className="label-caps">{isDefence ? "Relief sought by respondent *" : "Relief sought *"}</Label>
        <Textarea required value={f.relief_sought} onChange={(e) => set("relief_sought", e.target.value)} className="rounded-none min-h-[88px]" data-testid={`${kind}-relief`} /></div>
      <div className="space-y-2"><Label className="label-caps">{isDefence ? "Brief facts and reply on merits *" : "Brief facts *"}</Label>
        <Textarea required value={f.brief_facts} onChange={(e) => set("brief_facts", e.target.value)} className="rounded-none min-h-[140px]" data-testid={`${kind}-facts`} /></div>
      {isDefence && (
        <div className="space-y-2"><Label className="label-caps">Preliminary objections (jurisdiction, limitation, arbitrability)</Label>
          <Textarea value={f.preliminary_objections || ""} onChange={(e) => set("preliminary_objections", e.target.value)} className="rounded-none min-h-[72px]" data-testid={`${kind}-objections`} /></div>
      )}
      <MultiPdfPicker files={files} onChange={setFiles} label={`Attach the scanned ${KINDS[kind].label} and annexures (PDF)`} testId={`${kind}-pdfs`}
        progress={progress} merge={merge} onMergeChange={setMerge} />
      <Button type="submit" className={btn} data-testid={`${kind}-submit`}>{existing ? "File amended " : "File "}{KINDS[kind].label}</Button>
    </form>
  );
};

const PartyView = ({ title, p }) => (
  <div className="text-sm"><p className="label-caps">{title}</p>
    <p className="mt-1 font-medium">{p.name}</p><p className="text-muted-foreground whitespace-pre-wrap">{p.address}</p>
    <p className="text-muted-foreground">{[p.email, p.phone, p.representative].filter(Boolean).join(" · ")}</p></div>
);

const PleadingView = ({ p }) => (
  <div className="px-6 py-5 space-y-4" data-testid={`pleading-view-${p.kind}`}>
    <p className="text-xs text-muted-foreground">Filed by {p.filed_by_name} · {fmtDateTime(p.filed_at)} · <Badge className="bg-slate-100 text-slate-800">v{p.version}</Badge></p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5"><PartyView title="Claimant" p={p.claimant} /><PartyView title="Respondent" p={p.respondent} /></div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      <div><p className="label-caps">Claim amount</p><p className="mt-1 font-mono" data-testid={`${p.kind}-amount`}>{money(p.claim_amount)}</p></div>
      {p.counter_claim_amount != null && <div><p className="label-caps">Counter-claim</p><p className="mt-1 font-mono">{money(p.counter_claim_amount)}</p></div>}
    </div>
    <div className="text-sm"><p className="label-caps">Relief sought</p><p className="mt-1 whitespace-pre-wrap">{p.relief_sought}</p></div>
    <div className="text-sm"><p className="label-caps">Brief facts</p><p className="mt-1 whitespace-pre-wrap">{p.brief_facts}</p></div>
    {p.preliminary_objections && <div className="text-sm"><p className="label-caps">Preliminary objections</p><p className="mt-1 whitespace-pre-wrap">{p.preliminary_objections}</p></div>}
  </div>
);

export const StructuredPleadings = ({ caseId, items, kase, isArb, mySide, onReload }) => {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-6">
      {Object.entries(KINDS).map(([kind, meta]) => {
        const latest = items.find((p) => p.kind === kind);
        const canFile = !isArb && mySide === meta.side && kase.my_side_paid;
        return (
          <Section key={kind} title={`${meta.label} — ${meta.side}'s side`} testId={`pleading-section-${kind}`}
            form={canFile && (editing === kind || !latest) ? (
              <PleadingForm caseId={caseId} kind={kind} kase={kase} existing={editing === kind ? latest : null}
                onDone={() => { setEditing(null); onReload(); }} />
            ) : canFile && latest ? (
              <button onClick={() => setEditing(kind)} data-testid={`amend-${kind}`}
                className="text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400 hover:underline">Amend {meta.label}</button>
            ) : !isArb && mySide === meta.side && !kase.my_side_paid ? (
              <p className="text-xs text-muted-foreground" data-testid={`pleading-locked-${kind}`}>Filing opens once your side's half of the case fee is paid.</p>
            ) : null}>
            {latest ? <PleadingView p={latest} /> : (
              <p className="px-6 py-8 text-sm text-muted-foreground" data-testid={`pleading-empty-${kind}`}>Not yet filed.</p>
            )}
          </Section>
        );
      })}
    </div>
  );
};
